import { useEffect, useState } from "react";
import { CalendarSearch, AlertTriangle } from "lucide-react";
import { DatePickerHR } from "./DatePickerHR";
import api, { fmtNum, fmtDate } from "../lib/api";

const endOfMonth = () => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + 1, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const ProjectionCard = ({ fixedDate, compact = false }) => {
  const [date, setDate] = useState(fixedDate || endOfMonth());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (fixedDate) setDate(fixedDate);
  }, [fixedDate]);

  useEffect(() => {
    if (date) api.get("/projection", { params: { date } }).then((r) => setData(r.data)).catch(() => {});
  }, [date]);

  if (compact) {
    if (!data) return null;
    return (
      <div className="border border-dashed border-[#D97706]/60 rounded-sm p-3 space-y-1.5" data-testid="projection-compact">
        <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Očekivani saldo na ovaj dan</p>
        {data.banks.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-[#475569] flex items-center gap-1.5">
              {b.negative && <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626]" title="Saldo u ovoj valuti pada u minus — razmislite o prijenosu sredstava između banaka ili konverziji" />}
              {b.short}
            </span>
            <span className={`tabular-nums font-semibold ${b.negative ? "text-[#DC2626]" : "text-[#0F172A]"}`}>
              {fmtNum(b.projected_total_eur, "€")}
              <span className="text-xs font-normal text-[#64748B] ml-1.5">({fmtNum(b.projected_eur)} € / {fmtNum(b.projected_bam)} KM)</span>
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 text-sm border-t border-[#E2E8F0] pt-1.5">
          <span className="font-semibold text-[#0F172A]">Ukupno</span>
          <span className={`tabular-nums font-bold ${data.total_projected_eur < 0 ? "text-[#DC2626]" : "text-[#0D9488]"}`}>{fmtNum(data.total_projected_eur, "€")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="projection-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <CalendarSearch className="w-4 h-4 text-[#0D9488]" />
          <h2 className="font-heading text-lg font-semibold text-[#0F172A]">Projekcija salda na dan</h2>
        </div>
        <DatePickerHR data-testid="projection-date-input" value={date} onChange={(v) => setDate(v || endOfMonth())} className="w-44" />
      </div>
      <p className="text-xs text-[#64748B] mb-3">Trenutno stanje + svi planovi dospjeli do odabranog datuma. Crveno = banka pada u minus, potrebno prebaciti sredstva.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="projection-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#475569] bg-[#F1F5F9]">
              <th className="px-3 py-2 font-semibold">Banka</th>
              <th className="px-3 py-2 font-semibold text-right">Trenutno</th>
              <th className="px-3 py-2 font-semibold text-right">Priljev do datuma</th>
              <th className="px-3 py-2 font-semibold text-right">Odljev do datuma</th>
              <th className="px-3 py-2 font-semibold text-right">Očekivano {data ? fmtDate(data.date) : ""}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.banks || []).map((b) => (
              <tr key={b.id} data-testid={`projection-row-${b.short}`} className={`border-b border-[#F1F5F9] ${b.negative ? "bg-[#DC2626]/5" : ""}`}>
                <td className="px-3 py-2.5 font-medium flex items-center gap-1.5">
                  {b.negative && <AlertTriangle data-testid={`projection-warning-${b.short}`} className="w-4 h-4 text-[#DC2626]" title="Saldo u ovoj valuti pada u minus — razmislite o prijenosu sredstava između banaka ili konverziji" />}
                  {b.short}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#475569]">{fmtNum(b.current_total_eur, "€")}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#0D9488]">{b.planned_in_eur ? `+${fmtNum(b.planned_in_eur, "€")}` : "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-[#DC2626]">{b.planned_out_eur ? `−${fmtNum(b.planned_out_eur, "€")}` : "—"}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${b.negative ? "text-[#DC2626]" : "text-[#0F172A]"}`}>
                  {fmtNum(b.projected_total_eur, "€")}
                  <span className="block text-[11px] font-normal text-[#64748B]">{fmtNum(b.projected_eur)} € / {fmtNum(b.projected_bam)} KM</span>
                </td>
              </tr>
            ))}
            {data && (
              <tr className="bg-[#F8FAFC]">
                <td className="px-3 py-2.5 font-bold">Ukupno</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[#475569]">{fmtNum(data.total_current_eur, "€")}</td>
                <td colSpan={2}></td>
                <td data-testid="projection-total" className={`px-3 py-2.5 text-right tabular-nums font-bold text-base ${data.total_projected_eur < 0 ? "text-[#DC2626]" : "text-[#0D9488]"}`}>
                  {fmtNum(data.total_projected_eur, "€")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectionCard;
